/**
 * Meeting mixin — 統一處理「開啟會議室」邏輯（course-backstage 學員 web 端）
 *
 * 對應計畫 §4.1（仿 course-apis/web-pack 的同名 mixin 移植）
 *
 * 使用方式：
 *   import meetingMixin from '@/components/mixins/meeting'
 *   export default {
 *     mixins: [meetingMixin],
 *     methods: {
 *       openCourseRoom () {
 *         this.openMeetingForCourse(this.$data.courseId)
 *       }
 *     }
 *   }
 *
 * 流程：
 *   1. 呼叫後端 GET /meeting/getJoinUrl?type=...&id=...
 *   2. 若回應 gmailRequired === true：透過 EventBus 派發事件給全域 student-gmail-modal
 *   3. 否則 window.open(joinUrl)
 *
 * 依賴：
 *   - vue-resource interceptor 已自動 inject OAUTH-API-KEY header（main.js setup）
 *   - components/student-gmail-modal.vue 已全域註冊在 App.vue
 */
import Vue from 'vue'

// 跨元件溝通用的 EventBus（modal 訂閱 'show-gmail-modal' event）
export const MeetingEventBus = new Vue()

const WIN_OPEN_FEATURES = 'height=768,width=1024,top=0,left=0,toolbar=no,menubar=no,scrollbars=no,resizable=yes,location=no,status=no'

export default {
  data () {
    return {
      _meetingPending: null,
      _lastRequestParams: null
    }
  },
  methods: {
    /**
     * 開啟個教 / 體驗 / 團體課會議室
     *
     * @param {number|string} courseId  external course id (對應 courses.course_id / courses_main.course_id)
     */
    openMeetingForCourse (courseId) {
      this.$data._lastRequestParams = { type: 'course', id: courseId }
      this._fetchJoinUrl(this.$data._lastRequestParams).then((info) => {
        this._handleJoinResult(info, '正式課程環境')
      }).catch((err) => {
        this._showError(err, '開啟會議室失敗')
      })
    },

    /**
     * 開啟測試會議室
     */
    openTestRoom () {
      this.$data._lastRequestParams = { type: 'test' }
      this._fetchJoinUrl(this.$data._lastRequestParams).then((info) => {
        this._handleJoinResult(info, '環境測試會議室')
      }).catch((err) => {
        this._showError(err, '開啟測試會議室失敗')
      })
    },

    /**
     * 取得 YouTube 嵌入資訊（給 youtube-meet-embed 元件使用）
     */
    fetchEmbedInfo (courseId) {
      return new Promise((resolve, reject) => {
        this.$http.get('meeting/embedInfo', {
          params: { course_id: courseId }
        }).then((res) => {
          if (res.body && res.body.error_code === 0) {
            resolve(res.body.data)
          } else {
            reject(res.body)
          }
        }, (err) => reject(err))
      })
    },

    // ============================================================
    // 內部
    // ============================================================

    _fetchJoinUrl (params) {
      return new Promise((resolve, reject) => {
        this.$http.get('meeting/getJoinUrl', { params: params }).then((res) => {
          if (!res.body) {
            reject(new Error('empty response'))
            return
          }
          // 後端回 200 但 gmailRequired === true 也算成功流程
          if (res.body.error_code === 0 || res.body.gmailRequired === true) {
            resolve(res.body)
          } else {
            reject(res.body)
          }
        }, (err) => reject(err))
      })
    },

    _handleJoinResult (responseBody, windowTitle) {
      // 學生 Gmail 缺漏 → 派發 event 給全域 modal；modal 收完 Gmail 後回呼重試
      if (responseBody && responseBody.gmailRequired === true) {
        const retryParams = this.$data._lastRequestParams
        MeetingEventBus.$emit('show-gmail-modal', {
          message: responseBody.gmailRequiredMsg || '請先設定 Gmail 才能加入 Google Meet 會議',
          onSuccess: () => {
            if (retryParams) {
              this._fetchJoinUrl(retryParams).then((info) => this._handleJoinResult(info, windowTitle))
                .catch((e) => this._showError(e, '開啟會議室失敗'))
            }
          }
        })
        return
      }

      const data = responseBody && responseBody.data ? responseBody.data : null
      if (!data || !data.joinUrl) {
        this._showError({ message: '會議室尚未建立' }, '開啟會議室失敗')
        return
      }

      window.open(data.joinUrl, windowTitle, WIN_OPEN_FEATURES)
    },

    _showError (err, fallback) {
      const msg = (err && (err.message || (err.body && err.body.message))) || fallback || '操作失敗'
      console.error('[meetingMixin]', err)
      if (typeof window.alert === 'function') {
        window.alert(msg)
      }
    }
  }
}
